import { Button, ComboBox, DialogActions, Typography } from '@airbus/components-react';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useLanguageContextAPI } from '../../shared/context/LanguageContext';
import { postRequest } from '../../shared/utils';
import type { PcnrAssignmentsInput, PcnrInsertObject } from '../../shared/types';
import InfoTooltip from '../../shared/components/InfoTooltip';
import styles from './ProfitCenterSelection.module.css';

type ProfitCenterOption = { label: string; value: number };

type AssignedProfitCenter = { id: number; user_id: number; pcnr_id: number };

type ProfitCenterState = {
  profitCenterOptions: ProfitCenterOption[];
  selectedProfitCenters: ProfitCenterOption[];
  assignedProfitCenters: AssignedProfitCenter[];
  initialProfitCenterIds: number[];
  isProfitCenterLoading: boolean;
  isSavingProfitCenters: boolean;
};

type ProfitCenterSelectionProps = {
  open: boolean;
  userId?: number;
};

const sortProfitCenterOptions = (
  options: ProfitCenterOption[],
  selected: ProfitCenterOption[],
): ProfitCenterOption[] => {
  const selectedSet = new Set(selected.map((center) => center.value));
  return [...options].sort((a, b) => {
    const aSelected = selectedSet.has(a.value);
    const bSelected = selectedSet.has(b.value);
    if (aSelected !== bSelected) return aSelected ? -1 : 1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
};

const buildMutationVariables = (
  insertObjects: PcnrInsertObject[],
  deletedPcnr: number[],
  userId: number,
): PcnrAssignmentsInput | null => {
  const mutationVariables: PcnrAssignmentsInput = {};
  if (insertObjects.length) {
    mutationVariables.insertObjects = insertObjects;
  }
  if (deletedPcnr.length) {
    mutationVariables.deletedPcnr = deletedPcnr;
    mutationVariables.user_id = userId;
  }
  return Object.keys(mutationVariables).length ? mutationVariables : null;
};

const fetchProfitCenterData = async (userId: number) => {
  const [profitCenterResponse, assignedProfitCentersResponse] = await Promise.all([
    postRequest('/api/recordsQuery', {
      schema: 'npi',
      table: 'pcnr',
      columns: ['id', 'pc'],
      orderCol: 'pc',
      order: 'asc',
    }),
    postRequest('/api/recordsQuery', {
      schema: 'mbdcm',
      table: 'mbd_users_to_pcnr',
      columns: ['id', 'user_id', 'pcnr_id'],
      key: 'user_id',
      value: userId,
    }),
  ]);

  return { profitCenterResponse, assignedProfitCentersResponse };
};

const buildPcnrChanges = (
  assignedRows: AssignedProfitCenter[],
  selectedIds: number[],
  userId: number,
) => {
  const assignedIds = new Set(assignedRows.map((row) => row.pcnr_id));
  const selectedSet = new Set(selectedIds);
  const insertObjects: PcnrInsertObject[] = selectedIds
    .filter((pcnrId) => !assignedIds.has(pcnrId))
    .map((pcnrId) => ({
      user_id: userId,
      pcnr_id: pcnrId,
      created_by: userId,
      updated_by: userId,
      update_date: new Date().toISOString().substring(0, 19),
    }));
  const deletedPcnr = assignedRows
    .filter((row) => !selectedSet.has(row.pcnr_id))
    .map((row) => row.pcnr_id);

  return { insertObjects, deletedPcnr };
};

export default function ProfitCenterSelection(props: ProfitCenterSelectionProps) {
  const { open, userId } = props;
  const { t } = useLanguageContextAPI();

  const [state, setState] = useState<ProfitCenterState>({
    profitCenterOptions: [],
    selectedProfitCenters: [],
    assignedProfitCenters: [],
    initialProfitCenterIds: [],
    isProfitCenterLoading: false,
    isSavingProfitCenters: false,
  });

  const fetchProfitCenters = async () => {
    if (!userId) return;
    setState(
      produce((draft) => {
        draft.isProfitCenterLoading = true;
      }),
    );

    try {
      const { profitCenterResponse, assignedProfitCentersResponse } =
        await fetchProfitCenterData(userId);

      if (profitCenterResponse?.errors || assignedProfitCentersResponse?.errors) {
        console.error(
          'Error fetching profit centers:',
          profitCenterResponse?.errors,
          assignedProfitCentersResponse?.errors,
        );
        return;
      }

      const profitCenters =
        profitCenterResponse?.data?.['npi_pcnr']?.map((item) => ({
          label: item.pc,
          value: item.id,
        })) ?? [];
      const assignedRows = assignedProfitCentersResponse?.data?.['mbdcm_mbd_users_to_pcnr'] ?? [];
      const assignedProfitCenterIds = assignedRows.map((item) => item.pcnr_id);
      const selectedCenters = profitCenters.filter((center) =>
        assignedProfitCenterIds.includes(center.value),
      );

      setState(
        produce((draft) => {
          draft.profitCenterOptions = profitCenters;
          draft.assignedProfitCenters = assignedRows;
          draft.selectedProfitCenters = selectedCenters;
          draft.initialProfitCenterIds = assignedProfitCenterIds;
        }),
      );
    } catch (error) {
      console.error('profile error', error);
    } finally {
      setState(
        produce((draft) => {
          draft.isProfitCenterLoading = false;
        }),
      );
    }
  };

  useEffect(() => {
    if (!open || !userId) return;
    fetchProfitCenters();
  }, []);

  const compareNumbers = (a: number, b: number): number => a - b;
  const selectedProfitCenterIds = state.selectedProfitCenters
    .map((center) => center.value)
    .sort(compareNumbers);
  const sortedProfitCenterOptions = sortProfitCenterOptions(
    state.profitCenterOptions,
    state.selectedProfitCenters,
  );
  const initialProfitCenterIdsSorted = [...state.initialProfitCenterIds].sort(compareNumbers);
  const hasProfitCenterChanges =
    selectedProfitCenterIds.length !== initialProfitCenterIdsSorted.length ||
    selectedProfitCenterIds.some((value, index) => value !== initialProfitCenterIdsSorted[index]);

  const isProfitCenterSaveDisabled =
    state.isProfitCenterLoading || state.isSavingProfitCenters || !hasProfitCenterChanges;

  const handleSaveProfitCenters = async () => {
    if (!userId || isProfitCenterSaveDisabled) return;

    const { insertObjects, deletedPcnr } = buildPcnrChanges(
      state.assignedProfitCenters,
      selectedProfitCenterIds,
      userId,
    );

    const mutationVariables = buildMutationVariables(insertObjects, deletedPcnr, userId);
    if (!mutationVariables) return;

    try {
      setState(
        produce((draft) => {
          draft.isSavingProfitCenters = true;
        }),
      );
      await postRequest('/api/pcnrAssignments', {
        mutationVariables,
      });

      await fetchProfitCenters();
    } catch (error) {
      console.error('profile error', error);
    } finally {
      setState(
        produce((draft) => {
          draft.isSavingProfitCenters = false;
        }),
      );
    }
  };

  return (
    <>
      <div className={styles.sectionHeader}>
        <Typography variant='medium' className={styles.sectionTitle}>
          Profit Centers
        </Typography>
        <InfoTooltip label={t('profile.shift_select')} placement='right' />
      </div>

      <ComboBox
        multiple
        className={`w-100 ${styles.comboBox}`}
        options={sortedProfitCenterOptions}
        value={state.selectedProfitCenters}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        disabled={state.isProfitCenterLoading}
        onChange={(newValue) =>
          setState(
            produce((draft) => {
              draft.selectedProfitCenters = newValue ?? [];
            }),
          )
        }
      />

      <DialogActions>
        <Button
          variant='primary'
          onClick={handleSaveProfitCenters}
          disabled={isProfitCenterSaveDisabled}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </>
  );
}
